import { useEffect, useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import { useChat } from '@livekit/components-react'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { SendMessageInput } from 'modules/meetings/components/SendMessageInput'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useRoom } from 'modules/meetings/lib/hooks/useRoom'
import * as styles from 'modules/meetings/lib/utils/hiddenScrollBar.vanilla.css'

const linkifyText = (text: string): string => {
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.(com|es|org|net|edu|gov|io|co|uk|de|fr|it|nl|be|ch|at|se|no|dk|fi|pl|ru|jp|cn|in|br|mx|ar|cl|pe|ve|co\.uk|co\.jp|co\.nz|co\.za|com\.au|com\.br|com\.mx)([^\s]*))/gi

  return text.replace(urlRegex, (match) => {
    const href = match.startsWith('http') ? match : `https://${match}`
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${match}</a>`
  })
}

export const Chat = () => {
  const {
    access: { locale },
    employee: currentEmployee,
  } = useCurrentSession()
  const { chatMessages, send, isSending } = useChat()
  const previousMessageCountRef = useRef(0)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const { isChatOpen, addPendingMessage } = useRoom()
  const { meeting } = useMeeting()
  const [isHovering, setIsHovering] = useState(false)

  const attendees = meeting?.attendees || []

  useEffect(() => {
    if (chatMessages.length > previousMessageCountRef.current) {
      if (!isChatOpen) {
        addPendingMessage()
      }
    }
    previousMessageCountRef.current = chatMessages.length
  }, [chatMessages, isChatOpen, addPendingMessage])

  useEffect(() => {
    if (!isHovering && messagesContainerRef.current && chatMessages.length > 0) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [chatMessages, isHovering])

  const formattedMessages = chatMessages.map((msg) => {
    const attendee = attendees.find(
      ({ employee }) => Number(employee?.id) === Number(msg?.from?.identity)
    )
    const fullName = attendee?.employee?.fullName || i18n.t('meetings.unknown_participant')
    const avatar = buildUrl(attendee?.employee?.avatar?.url) || undefined

    return {
      from: {
        identity: msg?.from?.identity || '',
      },
      message: linkifyText(msg.message),
      timestamp: msg.timestamp.toString(),
      fullName,
      avatar,
    }
  })

  return (
    <>
      <Box padding="s20">
        <Text.Header2 weight="semibold" color="grey1000">
          {i18n.t('meetings.room.chat.title')}
        </Text.Header2>
      </Box>

      <Box
        ref={messagesContainerRef}
        overflowY="auto"
        flexGrow
        className={styles.hiddenScrollBar}
        paddingX="s20"
        paddingBottom="s20"
        style={{
          scrollBehavior: 'smooth',
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {formattedMessages.map((msg, index) => {
          const msgBefore = formattedMessages[index - 1] || null

          const shouldShowHeader =
            !msgBefore ||
            msg.from?.identity !== msgBefore.from?.identity ||
            (msg.from?.identity === msgBefore.from?.identity &&
              moment(msg.timestamp).diff(moment(msgBefore.timestamp), 'minutes') >= 2)

          return (
            <Box key={msg.timestamp} flexDirection="column" paddingBottom="s4">
              {shouldShowHeader && (
                <Box flexDirection="row" gap="s6" alignItems="center" paddingX="s4" paddingY="s8">
                  <F0AvatarPerson
                    firstName={msg.fullName}
                    lastName={msg.fullName}
                    src={msg.avatar}
                    size="sm"
                  />
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {msg.fullName}
                  </Text.Normal>
                  <Text.Normal size="200" weight="normal">
                    •
                  </Text.Normal>
                  <Text.Normal size="100" weight="normal">
                    {moment(msg.timestamp, 'x').locale(locale).format('LT')}
                  </Text.Normal>
                </Box>
              )}
              <Box
                paddingX="s12"
                paddingY="s8"
                borderRadius={{ all: 'abs008' }}
                background={
                  Number(msg.from?.identity) === currentEmployee.id ? 'grey200' : 'viridian100'
                }
                overflowX="hidden"
                overflowY="hidden"
              >
                <RichTextDisplay
                  content={msg.message}
                  title={moment(msg.timestamp, 'x').locale(locale).format('HH:mm:ss')}
                />
              </Box>
            </Box>
          )
        })}
      </Box>

      <Box paddingX="s12" paddingBottom="s12">
        <SendMessageInput
          onSend={(msg) => {
            track(trackingEvents.meetings.ChatSendMessage, {
              message: msg,
            })
            send(msg)
          }}
          isSending={isSending}
          placeholder={i18n.t('meetings.meeting.chat.write_message')}
        />
      </Box>
    </>
  )
}
